import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, watch } from 'vue';

import {
  BaseText,
  Heading,
  FilterOption,
  HelperViewStatus,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import { TodayPosition } from './components/TodayPosition';
import { HistoricalPosition } from './components/HistoricalPosition';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { InstrumentPositionFilters } from './data';
import { InstrumentPositionFilterTypes } from './types';
import {
  getInstrumentPositionHistorical,
  getInstrumentPosition,
  GetInstrumentPositionHistoricalResponse
} from '@/modules/trade/services/instruments';
import { TradeInstrumentPositionPrice } from '@/modules/trade/types/instrument';
import { isRequestError } from '@warrenbrasil/web-http';
import { StringFormatted, NumberFormatted } from '@/modules/trade/types';
import { moneyViewer as money } from '@/modules/common/helpers/currency';

interface Props {
  ticker: string;
  latestPrice: NumberFormatted;
  latestPriceDate: StringFormatted;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'InstrumentPosition',
  props: {
    ticker: null,
    latestPrice: null,
    latestPriceDate: null
  },
  setup(__props: any) {

const props = __props as {
  ticker: string;
  latestPrice: NumberFormatted;
  latestPriceDate: StringFormatted;
};



const selectedFilter = ref<InstrumentPositionFilterTypes>(
  InstrumentPositionFilterTypes.Today
);
const position = ref<
  TradeInstrumentPositionPrice | GetInstrumentPositionHistoricalResponse | null
>(null);
const updatedDate = ref<string | null>(null);
const isLoading = ref<boolean>(true);
const hasError = ref<boolean>(false);
const abortSignal = ref<AbortController | null>(null);

// Receives a new price in real time and updates the last update date
watch(
  () => props.latestPriceDate,
  () => {
    if (position.value && isTodayFilterSelected.value && props.latestPriceDate)
      updatedDate.value = props.latestPriceDate.formatted;
  }
);

// Receives a new price in real time and updates the user position with it
watch(
  () => props.latestPrice,
  () => {
    if (isTodayFilterSelected.value && props.latestPrice) {
      updateUserPosition(props.latestPrice);
    }
  }
);

const updateUserPosition = (newPrice: NumberFormatted) => {
  if (!position.value) return;

  position.value.price = newPrice;
  if (position.value.quantity) {
    const newCurrentValue = newPrice.value * position.value.quantity;
    position.value.currentValue = {
      value: newCurrentValue,
      formatted: money(newCurrentValue)
    };
  }
};

const handleFilterSelection = async (value: InstrumentPositionFilterTypes) => {
  if (value === selectedFilter.value) return;
  isLoading.value = true;
  selectedFilter.value = value;
  await fetchData();
};

const fetchData = async () => {
  if (abortSignal.value) {
    abortSignal.value.abort();
  }
  abortSignal.value = new AbortController();

  try {
    isLoading.value = true;
    hasError.value = false;
    let response = null;

    if (selectedFilter.value === InstrumentPositionFilterTypes.Today) {
      response = await getInstrumentPosition(
        props.ticker,
        abortSignal.value.signal
      );
    } else if (
      selectedFilter.value === InstrumentPositionFilterTypes.Historical
    ) {
      response = await getInstrumentPositionHistorical(
        props.ticker,
        abortSignal.value.signal
      );
    }

    position.value = response;
    updatedDate.value = response?.updatedAt?.formatted || '-';
  } catch (e) {
    if (isRequestError(e) && e.type === 'canceled') return;

    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const isLoadingInitialRequest = computed(() => {
  return isLoading.value && !hasError.value && !position.value;
});

const showPositionSection = computed(() => {
  return isLoading.value || hasError.value || position.value;
});

const isTodayFilterSelected = computed(() => {
  return selectedFilter.value === InstrumentPositionFilterTypes.Today;
});

onMounted(async () => {
  await fetchData();
});

return { __sfc: true,props, selectedFilter, position, updatedDate, isLoading, hasError, abortSignal, updateUserPosition, handleFilterSelection, fetchData, isLoadingInitialRequest, showPositionSection, isTodayFilterSelected, BaseText, Heading, FilterOption, HelperViewStatus, SkeletonLoader, TodayPosition, HistoricalPosition, NebraskaColors, InstrumentPositionFilters, InstrumentPositionFilterTypes }
}

})